/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { create } from 'zustand';

import { HelmetBase, HelmetUpdate } from '@/common/interface';
import { getAppSettings } from '@/services';

import siteInfoStore from './siteInfo';

interface HelmetStore {
  items: HelmetBase;
  update: (params: HelmetUpdate) => void;
}

const makePageTitle = (title = '', subtitle = '') => {
  const { siteInfo } = siteInfoStore.getState();
  if (!subtitle) {
    subtitle = `${siteInfo.name}`;
  }
  let pageTitle = subtitle;
  if (title && title !== subtitle) {
    pageTitle = `${title}${subtitle ? ` - ${subtitle}` : ''}`;
  }
  return pageTitle;
};

const makeDescription = async (title = '', description = '') => {
  const appSettings = await getAppSettings();
  if (title === appSettings.general?.name)
    return 'IntelliBlink 是专业的机器视觉与 AOI 技术社区。在这里您可以讨论深度学习、工业视觉算法及自动光学检测难题，与行业专家共同探索前沿技术解决方案。';
  if (title === '问题')
    return '浏览 IntelliBlink 最新的技术问答。涵盖机器视觉、OpenCV、PyTorch 及 AOI 缺陷检测等领域，获取专家级技术支持，提升工业视觉项目研发效率。';
  if (title === '标签')
    return '通过技术标签快速定位解决方案。从深度学习模型到 AOI 硬件选型，IntelliBlink 丰富的标签系统助您精准获取机器视觉与工业检测的分类技术文档。';
  if (title === '用户')
    return '连接 IntelliBlink 社区的机器视觉工程师与 AI 专家。查看行业大咖的个人主页、技术专长及贡献排行，拓展您的工业视觉专业人脉网络。';
  if (title === '登录')
    return '登录 IntelliBlink 账户，参与机器视觉与 AOI 技术的深度讨论。提问、回答、分享经验，与全球开发者共同推动工业自动检测技术的进步。';
  return description;
};

const pageTags = create<HelmetStore>((set) => ({
  items: {
    pageTitle: '',
    description: '',
    keywords: '',
  },
  update: async (params) => {
    const o: HelmetBase = {};
    if (params.title || params.subtitle) {
      o.pageTitle = makePageTitle(params.title, params.subtitle);
    }
    o.description =
      (await makeDescription(params.title, params.description)) ||
      siteInfoStore.getState().siteInfo?.description ||
      '';
    o.keywords = params.keywords || '';

    set({
      items: o,
    });
  },
}));

export default pageTags;
